import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { forumAPI } from '../services/api';
import ReactQuillEditor from '../components/ReactQuillEditor';
import GuestAuthBanner from '../components/GuestAuthBanner';
import {
  MessageSquare,
  Heart,
  Share2,
  Send,
  MoreVertical,
  Edit,
  Trash2,
  Image as ImageIcon,
  X,
  Loader2,
  Clock,
  ArrowLeft,
  ChevronLeft,
  Tag
} from 'lucide-react';

interface User {
  _id: string;
  name: string;
  email: string;
  role?: string;
}

interface Comment {
  user: string | User;
  content: string;
  createdAt: string;
  _id: string;
}

interface Post {
  _id: string;
  author: User;
  content: string;
  images?: string[];
  tags?: string[];
  category?: string;
  likes: Array<{ user: string | User; createdAt: string }>;
  comments: Comment[];
  shares?: Array<{ user: string | User; createdAt: string }>;
  isEdited?: boolean;
  createdAt: string;
  updatedAt: string;
}

const PostDetail: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentInput, setCommentInput] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingPost, setEditingPost] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editImages, setEditImages] = useState<string[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showPostMenu, setShowPostMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [deletingComment, setDeletingComment] = useState<string | null>(null);

  useEffect(() => {
    if (postId) loadPost();
  }, [postId]);

  const loadPost = async () => {
    if (!postId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await forumAPI.getPost(postId);
      if (response.data.success) {
        setPost(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load post');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) { setError('You must be logged in to like'); return; }
    if (!post) return;
    try {
      const response = await forumAPI.likePost(post._id);
      if (response.data.success) loadPost();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to like');
    }
  };

  const handleComment = async () => {
    if (!user) { setError('You must be logged in to comment'); return; }
    if (!commentInput.trim() || !post) return;
    setSubmittingComment(true);
    try {
      const response = await forumAPI.addComment(post._id, commentInput.trim());
      if (response.data.success) {
        setCommentInput('');
        loadPost();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!post) return;
    setDeletingComment(commentId);
    try {
      const response = await forumAPI.deleteComment(post._id, commentId);
      if (response.data.success) loadPost();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete comment');
    } finally {
      setDeletingComment(null);
    }
  };

  const handleShare = async () => {
    if (!user) { setError('You must be logged in to share'); return; }
    if (!post) return;
    try {
      const response = await forumAPI.sharePost(post._id);
      if (response.data.success) loadPost();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to share');
    }
  };

  const handleEditPost = async () => {
    if (!post) return;
    const textContent = editContent.replace(/<[^>]*>/g, '').trim();
    if (!textContent) { setError('Post content cannot be empty'); return; }
    setSavingEdit(true);
    try {
      const response = await forumAPI.updatePost(post._id, {
        content: editContent,
        images: editImages
      });
      if (response.data.success) {
        setEditingPost(false);
        setEditContent('');
        setEditImages([]);
        loadPost();
      } else {
        setError(response.data.message || 'Failed to update');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePost = async () => {
    if (!post) return;
    try {
      const response = await forumAPI.deletePost(post._id);
      if (response.data.success) {
        // Navigate back to category
        if (post.category) {
          navigate(`/forum/category/${encodeURIComponent(post.category)}`);
        } else {
          navigate('/forum');
        }
      } else {
        setError(response.data.message || 'Failed to delete');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const formatTimeAgo = (date: string) => {
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const isLiked = () => {
    if (!user || !post) return false;
    return post.likes.some(like => {
      if (!like?.user) return false;
      const likeUserId = typeof like.user === 'object' ? like.user._id : like.user;
      return likeUserId === user._id;
    });
  };

  const isShared = () => {
    if (!user || !post?.shares?.length) return false;
    return post.shares.some(share => {
      if (!share?.user) return false;
      const shareUserId = typeof share.user === 'object' ? share.user._id : share.user;
      return shareUserId === user._id;
    });
  };

  const isAuthor = () => user && post && (post.author?._id === user._id || user.role === 'admin');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error && !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!post) return null;

  const decodedCategory = post.category ? decodeURIComponent(post.category) : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <GuestAuthBanner message="Sign up or log in to like, comment, and engage with the community!" />

        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => navigate('/forum')}
            className="text-sm text-gray-500 hover:text-emerald-600 transition-colors"
          >
            Forum
          </button>
          <ChevronLeft className="h-3 w-3 text-gray-400 rotate-180" />
          {decodedCategory && (
            <>
              <button
                onClick={() => navigate(`/forum/category/${encodeURIComponent(post.category || '')}`)}
                className="text-sm text-gray-500 hover:text-emerald-600 transition-colors"
              >
                {decodedCategory}
              </button>
              <ChevronLeft className="h-3 w-3 text-gray-400 rotate-180" />
            </>
          )}
          <span className="text-sm font-medium text-gray-900 truncate">
            {post.content.replace(/<[^>]*>/g, '').trim().slice(0, 40) || 'Post'}
          </span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <div className="p-1 bg-red-100 rounded-lg">
              <X className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Post Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Post Header */}
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-3">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <span className="text-lg font-bold text-white">{post.author?.name?.charAt(0).toUpperCase() || '?'}</span>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900 text-lg">{post.author?.name || 'Unknown User'}</h3>
                    {post.author?.role === 'admin' && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">Admin</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{formatTimeAgo(post.createdAt)}</span>
                    {post.isEdited && <span className="text-gray-400">(edited)</span>}
                  </div>
                </div>
              </div>
              {isAuthor() && !editingPost && (
                <div className="relative">
                  <button
                    onClick={() => setShowPostMenu(!showPostMenu)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </button>
                  {showPostMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-10">
                      <button
                        onClick={() => { setEditingPost(true); setEditContent(post.content); setEditImages(post.images || []); setShowPostMenu(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center rounded-t-xl"
                      >
                        <Edit className="h-4 w-4 mr-2" /> Edit
                      </button>
                      <button
                        onClick={() => { setShowPostMenu(false); setShowDeleteConfirm(true); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center rounded-b-xl"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {editingPost ? (
              <div className="mb-4">
                <ReactQuillEditor
                  value={editContent}
                  onChange={setEditContent}
                  className="bg-white rounded-lg"
                  style={{ minHeight: '200px' }}
                />
                {editImages.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {editImages.map((image, idx) => (
                      <div key={idx} className="relative group">
                        <img src={image} alt={`Edit ${idx + 1}`} className="w-full h-32 object-cover rounded-lg" />
                        <button
                          onClick={() => setEditImages(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center space-x-2 mt-3">
                  <button
                    onClick={handleEditPost}
                    disabled={savingEdit}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {savingEdit ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setEditingPost(false); setEditContent(''); setEditImages([]); }}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-gray-800 mb-4 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: (post.content || '').replace(/\n/g, '<br>') }} />
                {post.tags?.length ? (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag, idx) => (
                      <span key={idx} className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm border border-emerald-200">
                        <Tag className="h-3 w-3 mr-1" />{tag}
                      </span>
                    ))}
                  </div>
                ) : null}
                {post.images?.length ? (
                  <div className={`grid gap-3 mb-4 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {post.images.map((image, idx) => (
                      <img
                        key={idx}
                        src={image}
                        alt={`Post ${idx + 1}`}
                        className="w-full max-h-[600px] object-contain rounded-lg cursor-pointer hover:opacity-90 bg-gray-50"
                        onClick={() => setSelectedImage(image)}
                      />
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>

          {/* Action Bar */}
          {!editingPost && (
            <div className="border-t border-gray-100 px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <button
                    onClick={handleLike}
                    className={`flex items-center space-x-2 transition-colors ${isLiked() ? 'text-rose-500' : 'text-gray-500 hover:text-rose-500'}`}
                  >
                    <Heart className={`h-5 w-5 ${isLiked() ? 'fill-current' : ''}`} />
                    <span className="text-sm font-medium">{post.likes?.length || 0}</span>
                  </button>
                  <button className="flex items-center space-x-2 text-gray-500 hover:text-emerald-600 transition-colors">
                    <MessageSquare className="h-5 w-5" />
                    <span className="text-sm font-medium">{post.comments?.length || 0}</span>
                  </button>
                  <button
                    onClick={handleShare}
                    className={`flex items-center space-x-2 transition-colors ${isShared() ? 'text-emerald-600' : 'text-gray-500 hover:text-emerald-600'}`}
                  >
                    <Share2 className={`h-5 w-5 ${isShared() ? 'fill-current' : ''}`} />
                    <span className="text-sm font-medium">{post.shares?.length || 0}</span>
                  </button>
                </div>
                {decodedCategory && (
                  <button
                    onClick={() => navigate(`/forum/category/${encodeURIComponent(post.category || '')}`)}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    ← Back to {decodedCategory}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Comments Section */}
        {!editingPost && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-600" />
              Comments ({post.comments?.length || 0})
            </h3>

            {/* Add Comment */}
            {user && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-white">{user.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={commentInput}
                      onChange={(e) => setCommentInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); } }}
                      placeholder="Write a comment..."
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                      rows={2}
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={handleComment}
                        disabled={!commentInput.trim() || submittingComment}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center text-sm font-medium"
                      >
                        {submittingComment ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Posting...</> : <><Send className="h-4 w-4 mr-1" /> Comment</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Comments List */}
            {post.comments?.length ? (
              <div className="space-y-3">
                {post.comments.map((comment, idx) => {
                  const commentUser = typeof comment.user === 'object' ? comment.user : null;
                  const isCommentAuthor = user && (
                    (commentUser && commentUser._id === user._id) ||
                    user.role === 'admin'
                  );

                  return (
                    <div key={comment._id || idx} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                      <div className="flex items-start space-x-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-white">
                            {commentUser?.name?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-gray-900">
                                {commentUser?.name || 'Unknown'}
                              </span>
                              {commentUser?.role === 'admin' && (
                                <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-medium">Admin</span>
                              )}
                              <span className="text-xs text-gray-400">{formatTimeAgo(comment.createdAt)}</span>
                            </div>
                            {isCommentAuthor && comment._id && (
                              <button
                                onClick={() => handleDeleteComment(comment._id!)}
                                disabled={deletingComment === comment._id}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                title="Delete comment"
                              >
                                {deletingComment === comment._id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{comment.content}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
                <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No comments yet. Be the first to share your thoughts!</p>
              </div>
            )}
          </div>
        )}

        {/* Image Lightbox */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4" onClick={() => setSelectedImage(null)}>
            <div className="relative max-w-4xl w-full">
              <button onClick={() => setSelectedImage(null)} className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors">
                <X className="h-6 w-6" />
              </button>
              <img src={selectedImage} alt="Full size" className="w-full max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Post</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this post? This action cannot be undone.</p>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePost}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 flex items-center text-sm font-medium shadow-sm"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDetail;